import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GenericStudentCardComponent } from '../generic-student-card/generic-student-card.component';
import { AdminActionsComponent } from '../student-card-actions/admin-actions/admin-actions.component';
import { EndorsedActionsComponent} from '../student-card-actions/endorsed-actions/endorsed-actions.component';
import { SchoolRosterStudent } from '@k12/models';
import { CostCorrectionDialogComponent } from './cost-correction-dialog.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-student-card',
  template: `
    <lib-generic-student-card
      #card
      [editable]="editable"
      [student]="student"
      [schoolTermGrades]="schoolTermGrades"
      [schoolGradeYears]="schoolGradeYears"
      [attendanceTypes]="attendanceTypes"
      [isExpanded]="isExpanded"
      [lockPriorTermCosts]="!showAdminActions"
      (toggleExpand)="toggleExpand.emit($event)">
      @if(editable) { 
        <lib-endorsed-actions
          studentCardActions
          [student]="student"
          (withdrawalRequested)="onWithdrawal($event)"
          (costCorrectionRequested)="onCostCorrection($event)">
        </lib-endorsed-actions>
      }
      @if (showAdminActions) {
        <lib-admin-actions
          studentCardActions
          [student]="student"
          [form]="card.form"
          [isNotAttending]="card.isNotAttending"
          [disableRevert]="disableBackwardMove"
          [individualCostErrors]="card.individualCostErrors"
          (saved)="onSaved($event)"
          (reverted)="onReverted($event)"
          (validateForm)="card.validateIndividualCost()"
          (syncFormToModel)="card.syncFormToModel()">
        </lib-admin-actions>
      }
    </lib-generic-student-card>
  `,
  standalone: true,
  imports: [GenericStudentCardComponent, AdminActionsComponent, EndorsedActionsComponent]
})
export class StudentCardComponent extends GenericStudentCardComponent {
  @Input() override editable = false;
  @Input() override isExpanded = false;
  @Input() showAdminActions = false;
  @Input() disableBackwardMove = false;
  @Input() onSavedCallback?: (studentEnrollmentId: string) => void;
  @Input() onRevertedCallback?: (studentEnrollmentId: string) => void;
  @Input() onWithdrawalCallback?: (student: SchoolRosterStudent) => void;
  @Output() saved = new EventEmitter<string>();
  @Output() reverted = new EventEmitter<string>();
  @Output() override withdrawalRequested = new EventEmitter<SchoolRosterStudent>();
  @Output() costCorrectionRequested = new EventEmitter<SchoolRosterStudent>();

  private readonly dialog = inject(MatDialog);

  get studentCardComponent() {
    return StudentCardComponent;
  }

  protected onCostCorrection(student: SchoolRosterStudent): void {
    this.dialog.open(CostCorrectionDialogComponent, {
      data: { student },
      width: '80vw',
      maxWidth: '1024px',
      disableClose: true
    });
  }

  constructor() {
    super();
  }

  onSaved(studentEnrollmentId: string): void {
    this.saved.emit(studentEnrollmentId);
    if (this.onSavedCallback) {
      this.onSavedCallback(studentEnrollmentId);
    }
  }

  onReverted(studentEnrollmentId: string): void {
    this.reverted.emit(studentEnrollmentId);
    if (this.onRevertedCallback) {
      this.onRevertedCallback(studentEnrollmentId);
    }
    if (this.onActionCompleted) {
      this.onActionCompleted(studentEnrollmentId);
    }
  }

  override onWithdrawal(student: SchoolRosterStudent): void {
    this.withdrawalRequested.emit(student);
    if (this.onWithdrawalCallback) {
      this.onWithdrawalCallback(student);
    }
  }
}
